import {route} from "quasar/wrappers";
import {createMemoryHistory, createRouter, createWebHashHistory, createWebHistory,} from "vue-router";
import {ADMIN_ROLES} from "src/constants";
import { store as appStore } from "src/boot/vuex";
import routes from "./routes";

/**
 * Gate routes that opt-in via `meta.requiresSuperAdmin: true`. Reads the
 * connected admin role through the `adminRole` getter (single hop — Law of
 * Demeter) so the store can change shape without breaking us. Routes that
 * don't set the meta flag pass straight through — this guard is additive.
 *
 * Redirect target: `/admin`. A non-super-admin who lands on `/admin/treasury`
 * is sent back to the project-curation surface they ARE allowed to see,
 * rather than the public home page (which would hide that they tried).
 */
function buildSuperAdminGuard(store) {
  return (to, _from, next) => {
    const requiresSuperAdmin = to.matched.some(
      (record) => record.meta && record.meta.requiresSuperAdmin
    );
    if (!requiresSuperAdmin) {
      return next();
    }
    const adminRole = store && store.getters ? store.getters.adminRole : null;
    if (adminRole === ADMIN_ROLES.SUPER_ADMIN) {
      return next();
    }
    return next({ path: "/admin" });
  };
}

export { buildSuperAdminGuard };

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function () {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === "history"
    ? createWebHistory
    : createWebHashHistory;

  const router = createRouter({
    routes,
    history: createHistory(process.env.VUE_ROUTER_BASE),
    scrollBehavior(to, _from, _savedPosition) {
      if (to.hash) {
        try {
          const el = document.querySelector(to.hash);
          if (el) {
            return window.scrollTo({
              top: el.offsetTop,
              behavior: "smooth",
            });
          }
        } catch {
        }
      } else if (to.path === "/" || to.path === "/token") {
        return window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        return {x: 0, y: 0};
      }
    },
  });

  if (router && typeof router.beforeEach === "function") {
    router.beforeEach(buildSuperAdminGuard(appStore));
  }

  return router;
});
